import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { COIN_DATABASE, getCoinInfo } from '../data/coinDatabase';

const CryptoContext = createContext();

// Fallback if env not loaded
const BINANCE_WS_BASE = process.env.REACT_APP_BINANCE_WS_URL || 'wss://stream.binance.com:9443/ws';
const WS_URL = `${BINANCE_WS_BASE}/!ticker@arr`;

// Create initial coin data from database (all coins with default values)
const createInitialCoins = () => {
    return COIN_DATABASE.map((coin, index) => ({
        symbol: coin.symbol,
        name: coin.name,
        shortName: coin.shortName,
        price: 0,
        priceChange: 0,
        priceChangePercent: 0,
        high24h: 0,
        low24h: 0,
        volume: 0,
        quoteVolume: 0,
        dbIndex: index,
        hasData: false, // Flag to track if we have real data
    }));
};

// Create a Map for O(1) lookup
const COIN_INDEX_MAP = new Map(COIN_DATABASE.map((c, index) => [c.symbol, index]));

const MAX_PRICE_HISTORY = 20; // Keep last 20 price points for sparkline

const readTickerValue = (...values) => {
    for (const value of values) {
        const parsed = parseFloat(value);
        if (Number.isFinite(parsed)) return parsed;
    }

    return 0;
};

export function CryptoProvider({ children }) {
    // Use ref to store coin data (for merging without triggering re-renders)
    const coinDataRef = useRef(new Map());
    // Track previous prices for flash animation
    const prevPricesRef = useRef(new Map());
    // Track price history for sparklines
    const priceHistoryRef = useRef(new Map());

    const [coins, setCoins] = useState(createInitialCoins);
    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('cryptoFavorites');
        return saved ? JSON.parse(saved) : ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'XRPUSDT'];
    });

    const wsRef = useRef(null);
    const reconnectTimeout = useRef(null);

    const publishCoins = useCallback(() => {
        const updatedCoins = COIN_DATABASE.map((dbCoin, index) => {
            const liveData = coinDataRef.current.get(dbCoin.symbol);
            if (liveData) return liveData;

            return {
                symbol: dbCoin.symbol,
                name: dbCoin.name,
                shortName: dbCoin.shortName,
                price: 0,
                priceChange: 0,
                priceChangePercent: 0,
                high24h: 0,
                low24h: 0,
                volume: 0,
                quoteVolume: 0,
                dbIndex: index,
                hasData: false,
            };
        });

        setCoins(updatedCoins);
        setLastUpdated(new Date());
        setLoading(false);
    }, []);

    const applyTicker = useCallback((ticker) => {
        const symbol = ticker.s;
        const dbIndex = COIN_INDEX_MAP.get(symbol);

        if (dbIndex === undefined) return false;

        const coinInfo = COIN_DATABASE[dbIndex] || getCoinInfo(symbol);
        const newPrice = readTickerValue(ticker.c, ticker.lastPrice);
        const prevPrice = prevPricesRef.current.get(symbol) || newPrice;

        let priceDirection = 'none';
        if (newPrice > prevPrice) priceDirection = 'up';
        else if (newPrice < prevPrice) priceDirection = 'down';

        prevPricesRef.current.set(symbol, newPrice);

        const history = priceHistoryRef.current.get(symbol) || [];
        const newHistory = newPrice > 0 ? [...history, newPrice].slice(-MAX_PRICE_HISTORY) : history;
        priceHistoryRef.current.set(symbol, newHistory);

        coinDataRef.current.set(symbol, {
            symbol,
            name: coinInfo?.name || symbol.replace('USDT', ''),
            shortName: coinInfo?.shortName || symbol.replace('USDT', ''),
            price: newPrice,
            priceChange: readTickerValue(ticker.p, ticker.priceChange),
            priceChangePercent: readTickerValue(ticker.P, ticker.priceChangePercent),
            high24h: readTickerValue(ticker.h, ticker.highPrice),
            low24h: readTickerValue(ticker.l, ticker.lowPrice),
            volume: readTickerValue(ticker.v, ticker.volume),
            quoteVolume: readTickerValue(ticker.q, ticker.quoteVolume),
            dbIndex,
            hasData: newPrice > 0,
            priceDirection,
            lastUpdate: Date.now(),
            priceHistory: newHistory,
        });

        return true;
    }, []);

    const connect = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) return;

        try {
            wsRef.current = new WebSocket(WS_URL);

            wsRef.current.onopen = () => {
                setConnected(true);
                setError(null);
                setLoading(false);
            };

            wsRef.current.onmessage = (event) => {
                const data = JSON.parse(event.data);

                // Update only coins that are in our database
                const tickers = Array.isArray(data) ? data : [data];
                let hasUpdates = false;
                tickers.forEach(ticker => {
                    if (applyTicker(ticker)) hasUpdates = true;
                });

                if (hasUpdates) publishCoins();
            };

            wsRef.current.onerror = () => {
                setError('Bağlantı hatası');
                setLoading(false);
            };

            wsRef.current.onclose = () => {
                setConnected(false);
                setLoading(false);
                reconnectTimeout.current = setTimeout(connect, 3000);
            };
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [applyTicker, publishCoins]);

    const disconnect = useCallback(() => {
        if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        if (wsRef.current) {
            wsRef.current.close();
            wsRef.current = null;
        }
    }, []);

    const toggleFavorite = (symbol) => {
        setFavorites(prev => {
            const newFavorites = prev.includes(symbol)
                ? prev.filter(s => s !== symbol)
                : [...prev, symbol];
            localStorage.setItem('cryptoFavorites', JSON.stringify(newFavorites));
            return newFavorites;
        });
    };

    const getFavoriteCoins = () => coins.filter(coin => favorites.includes(coin.symbol));

    const searchCoins = (query) => {
        if (!query) return coins;
        const q = query.toUpperCase();
        return coins.filter(c =>
            c.symbol.includes(q) ||
            c.name.toUpperCase().includes(q) ||
            c.shortName.includes(q)
        );
    };

    useEffect(() => {
        connect();
        return () => disconnect();
    }, [connect, disconnect]);

    return (
        <CryptoContext.Provider value={{
            coins,
            loading,
            connected,
            error,
            lastUpdated,
            favorites,
            toggleFavorite,
            getFavoriteCoins,
            searchCoins,
            reconnect: connect
        }}>
            {children}
        </CryptoContext.Provider>
    );
}

export function useCrypto() {
    const context = useContext(CryptoContext);
    if (!context) throw new Error('useCrypto must be used within CryptoProvider');
    return context;
}

export default CryptoContext;
